import ExcelJS from 'exceljs';
import { Chart as ChartJS } from 'chart.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatBillingCurrency } from '../../utils/billingRound';
import type { AzureCustomerCostResult } from '../../types/azureCost';
import { buildAzureCostObservations } from './azureCostObservations';
import {
  msrObservationWordTokens,
  normalizeMsrObservation,
  parseMsrObservationSegments,
  type MsrWordToken,
} from './msrObservationParser';
import type { MsrObservationEntry } from './msrReportTypes';
import { captureMsrElement, msrSectionFilename } from './msrReportSectionExport';

export type AzureCostSectionKey = 'azure-subscription' | 'azure-3months' | 'azure-3months-analysis';

export type AzureCostSectionExport = { blob: Blob; fileName: string };

const MSR_BLUE = 'FF3465A4';
const MSR_BLUE_RGB = { r: 52, g: 101, b: 164 };
const SLATE_600 = { r: 71, g: 85, b: 105 };
const SLATE_400 = { r: 148, g: 163, b: 184 };
const OBS_BG = { r: 248, g: 250, b: 252 };

const TABLE_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFF00' },
};

const OBSERVATION_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: MSR_BLUE },
};

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  bottom: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  left: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  right: { style: 'thin', color: { argb: 'FFB4B4B4' } },
};

const AZURE_ANALYSIS_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFBDD7EE' },
};

const AZURE_ANALYSIS_INCREASED_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFCE4B2' },
};

const AZURE_ANALYSIS_DECREASED_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFC6EFCE' },
};

const AZURE_COST_SECTION_CONFIG: Record<
  AzureCostSectionKey,
  { chartId: string; title: string; fileSlug: string; chartLabel: string }
> = {
  'azure-subscription': {
    chartId: 'msr-chart-azure-cost',
    title: 'Azure Subscription Cost Analysis',
    fileSlug: 'Azure_Subscription_Cost',
    chartLabel: 'Azure accumulated cost',
  },
  'azure-3months': {
    chartId: 'msr-chart-azure-cost-3months',
    title: 'Azure cost report for last 3 months',
    fileSlug: 'Azure_Cost_3_Months',
    chartLabel: 'Azure Cost Analysis Report',
  },
  'azure-3months-analysis': {
    chartId: 'msr-azure-cost-3months-analysis-table',
    title: 'Azure cost analysis for last 3 months',
    fileSlug: 'Azure_Cost_3_Months_Analysis',
    chartLabel: 'Azure cost analysis table',
  },
};

const AZURE_CAPTURE_IDS = [
  'msr-chart-azure-cost',
  'msr-chart-azure-cost-3months',
  'msr-azure-cost-3months-analysis-table',
];

function monthYearLongLabel(month: number, year: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function restoreChartsInElement(el: HTMLElement): void {
  for (const canvas of el.querySelectorAll('canvas')) {
    const chart = ChartJS.getChart(canvas);
    if (chart) {
      chart.resize();
      chart.update();
    }
  }
}

async function withAzureCostExportCapture<T>(work: () => Promise<T>): Promise<T> {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  try {
    return await work();
  } finally {
    window.scrollTo(scrollX, scrollY);
    for (const id of AZURE_CAPTURE_IDS) {
      const el = document.getElementById(id);
      if (el) restoreChartsInElement(el);
    }
  }
}

function pdfAsciiSafeText(s: string): string {
  return s
    .replace(/\u2192/g, ' to ')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/…/g, '...');
}

function styleTableHeader(cell: ExcelJS.Cell): void {
  cell.font = { bold: true };
  cell.fill = TABLE_HEADER_FILL;
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  cell.border = THIN_BORDER;
}

function styleTableBodyCell(cell: ExcelJS.Cell, alignRight = false): void {
  cell.alignment = { horizontal: alignRight ? 'right' : 'left', vertical: 'top', wrapText: true };
  cell.border = THIN_BORDER;
}

function writeObservationRichTextCell(ws: ExcelJS.Worksheet, row: number, text: string): void {
  const cell = ws.getCell(row, 1);
  const segments = parseMsrObservationSegments(text);
  cell.value = {
    richText: segments.map((seg) => ({
      text: seg.text,
      font: { bold: seg.bold, size: 10, color: { argb: 'FF334155' } },
    })),
  };
  cell.alignment = { wrapText: true, vertical: 'top' };
  cell.border = THIN_BORDER;
  ws.mergeCells(row, 1, row, 5);
  ws.getRow(row).height = Math.max(30, Math.ceil(text.length / 80) * 15);
}

function writeObservations(
  ws: ExcelJS.Worksheet,
  startRow: number,
  observations: MsrObservationEntry[]
): number {
  if (observations.length === 0) return startRow;

  let row = startRow;
  const header = ws.getCell(row, 1);
  header.value = 'Observation';
  header.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
  header.fill = OBSERVATION_HEADER_FILL;
  header.alignment = { horizontal: 'left', vertical: 'middle' };
  ws.mergeCells(row, 1, row, 5);
  row += 1;

  for (const entry of observations) {
    const { text, children } = normalizeMsrObservation(entry);
    writeObservationRichTextCell(ws, row, `• ${text}`);
    row += 1;

    for (const child of children ?? []) {
      writeObservationRichTextCell(ws, row, `    • ${child}`);
      row += 1;
    }
  }

  return row + 1;
}

function writeLabelValueTable(
  ws: ExcelJS.Worksheet,
  startRow: number,
  title: string,
  rows: Array<{ label: string; value: string }>
): number {
  let row = startRow;
  ws.getCell(row, 1).value = title;
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  row += 1;

  styleTableHeader(ws.getCell(row, 1));
  ws.getCell(row, 1).value = 'Item';
  styleTableHeader(ws.getCell(row, 2));
  ws.getCell(row, 2).value = 'Value';
  row += 1;

  for (const item of rows) {
    ws.getCell(row, 1).value = item.label;
    ws.getCell(row, 2).value = item.value;
    styleTableBodyCell(ws.getCell(row, 1));
    styleTableBodyCell(ws.getCell(row, 2));
    row += 1;
  }

  return row + 1;
}

function writeTwoColumnTable(
  ws: ExcelJS.Worksheet,
  startRow: number,
  startCol: number,
  title: string,
  col1: string,
  col2: string,
  rows: Array<{ c1: string; c2: string }>
): number {
  let row = startRow;
  ws.getCell(row, startCol).value = title;
  ws.getCell(row, startCol).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  row += 1;

  styleTableHeader(ws.getCell(row, startCol));
  ws.getCell(row, startCol).value = col1;
  styleTableHeader(ws.getCell(row, startCol + 1));
  ws.getCell(row, startCol + 1).value = col2;
  row += 1;

  if (rows.length === 0) {
    ws.getCell(row, startCol).value = 'No data';
    ws.mergeCells(row, startCol, row, startCol + 1);
    styleTableBodyCell(ws.getCell(row, startCol));
    return row + 1;
  }

  for (const item of rows) {
    ws.getCell(row, startCol).value = item.c1;
    ws.getCell(row, startCol + 1).value = item.c2;
    styleTableBodyCell(ws.getCell(row, startCol));
    styleTableBodyCell(ws.getCell(row, startCol + 1), true);
    row += 1;
  }

  return row;
}

async function writeSingleChartImage(
  ws: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  chartId: string,
  chartLabel: string,
  startRow: number
): Promise<number> {
  const capture = await captureMsrElement(chartId);
  let row = startRow;

  ws.getCell(row, 1).value = 'Chart';
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  row += 1;
  ws.getCell(row, 1).value = chartLabel;
  ws.getCell(row, 1).font = { bold: true, size: 11 };
  row += 1;

  if (!capture?.base64) {
    ws.getCell(row, 1).value =
      'Chart capture unavailable. Ensure this section is visible on screen and try again.';
    ws.mergeCells(row, 1, row, 5);
    return row + 2;
  }

  const width = 520;
  const height = Math.max(220, Math.round(width / capture.aspectRatio));
  const imageId = workbook.addImage({ base64: capture.base64, extension: 'png' });
  ws.addImage(imageId, {
    tl: { col: 0, row: row - 1 },
    ext: { width, height },
  });

  const rowsForImage = Math.ceil(height / 18) + 1;
  for (let i = 0; i < rowsForImage; i++) {
    ws.getRow(row + i).height = 18;
  }

  return row + rowsForImage + 1;
}

function drawSlideHeader(
  doc: jsPDF,
  customerName: string,
  reportTitle: string,
  contentW: number
): number {
  let y = 12;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(MSR_BLUE_RGB.r, MSR_BLUE_RGB.g, MSR_BLUE_RGB.b);
  const customerLines = doc.splitTextToSize(pdfAsciiSafeText(customerName.toUpperCase()), contentW);
  doc.text(customerLines, 12, y + 6);
  y += customerLines.length * 7 + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);
  const titleLines = doc.splitTextToSize(pdfAsciiSafeText(reportTitle.toUpperCase()), contentW);
  doc.text(titleLines, 12, y + 4);
  y += titleLines.length * 5 + 4;

  doc.setTextColor(0, 0, 0);
  return y;
}

function drawSingleChart(
  doc: jsPDF,
  capture: Awaited<ReturnType<typeof captureMsrElement>>,
  y: number,
  contentW: number
): number {
  if (!capture?.dataUrl) return y + 4;

  const maxH = 100;
  let imgW = contentW;
  let imgH = imgW / capture.aspectRatio;
  if (imgH > maxH) {
    imgH = maxH;
    imgW = imgH * capture.aspectRatio;
  }
  const x = 12 + (contentW - imgW) / 2;
  doc.addImage(capture.dataUrl, 'PNG', x, y, imgW, imgH);
  return y + imgH + 6;
}

function loadCaptureImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load capture image'));
    img.src = dataUrl;
  });
}

async function captureAnalysisTableElement(
  elementId: string
): Promise<{ dataUrl: string; aspectRatio: number; base64: string } | null> {
  const el = document.getElementById(elementId);
  if (!el) return null;

  const prevOverflow = el.style.overflow;
  const prevWidth = el.style.width;
  el.style.overflow = 'visible';
  el.style.width = 'max-content';

  try {
    if (!elementInViewport(el)) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      await new Promise((resolve) => setTimeout(resolve, 120));
    }

    const width = Math.max(el.scrollWidth, el.getBoundingClientRect().width);
    const height = Math.max(el.scrollHeight, el.getBoundingClientRect().height);
    if (width < 2 || height < 2) return null;

    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
    });
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1]! : dataUrl;
    return {
      dataUrl,
      base64,
      aspectRatio: canvas.width / Math.max(canvas.height, 1),
    };
  } catch {
    return null;
  } finally {
    el.style.overflow = prevOverflow;
    el.style.width = prevWidth;
  }
}

function elementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
}

/** Scale table capture to full content width and available page height; slice across pages when needed. */
async function drawAnalysisTableCapture(
  doc: jsPDF,
  capture: { dataUrl: string } | null,
  y: number,
  contentW: number,
  pageH: number
): Promise<number> {
  if (!capture?.dataUrl) return y + 4;

  const marginX = 12;
  const footerMargin = 16;
  const img = await loadCaptureImage(capture.dataUrl);
  const pxW = img.naturalWidth;
  const pxH = img.naturalHeight;
  const ar = pxW / Math.max(pxH, 1);

  const imgW = contentW;
  const imgH = imgW / ar;
  const mmPerPx = imgH / pxH;

  let availH = pageH - y - footerMargin;
  let cursorY = y;
  let srcY = 0;
  let lastSliceMmH = 0;

  while (srcY < pxH) {
    const slicePxH = Math.min(pxH - srcY, availH / mmPerPx);
    const sliceMmH = slicePxH * mmPerPx;
    lastSliceMmH = sliceMmH;

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = pxW;
    sliceCanvas.height = Math.ceil(slicePxH);
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.drawImage(img, 0, srcY, pxW, slicePxH, 0, 0, pxW, slicePxH);
    doc.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', marginX, cursorY, imgW, sliceMmH);

    srcY += slicePxH;
    if (srcY < pxH) {
      doc.addPage();
      cursorY = 12;
      availH = pageH - cursorY - footerMargin;
    }
  }

  return cursorY + lastSliceMmH + 6;
}

function addAzurePdfFooter(doc: jsPDF, pageIndex: number, pageCount: number): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(SLATE_400.r, SLATE_400.g, SLATE_400.b);
  doc.text(
    pdfAsciiSafeText(`Copyright © ${new Date().getFullYear()} Tenarai. All Rights Reserved.`),
    12,
    pageH - 8
  );
  if (pageCount > 1) {
    doc.text(`${pageIndex} / ${pageCount}`, pageW - 12, pageH - 8, { align: 'right' });
  } else {
    doc.text(String(pageIndex), pageW - 12, pageH - 8, { align: 'right' });
  }
  doc.setTextColor(0, 0, 0);
}

type PdfObsLine = MsrWordToken[];

function measureTokenWidth(doc: jsPDF, token: MsrWordToken, fontSize: number): number {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
  return doc.getTextWidth(pdfAsciiSafeText(token.text));
}

function wrapObservationParagraph(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  fontSize: number,
  bulletPrefix: string
): PdfObsLine[] {
  const tokens = msrObservationWordTokens(parseMsrObservationSegments(text));
  const lines: PdfObsLine[] = [];
  let current: MsrWordToken[] = [];
  let currentW = 0;

  const flush = () => {
    if (current.length > 0) {
      lines.push(current);
      current = [];
      currentW = 0;
    }
  };

  const startContinuation = () => {
    const indent: MsrWordToken = { text: '   ', bold: false };
    current = [indent];
    currentW = measureTokenWidth(doc, indent, fontSize);
  };

  if (bulletPrefix) {
    const bullet: MsrWordToken = { text: bulletPrefix, bold: false };
    current.push(bullet);
    currentW = measureTokenWidth(doc, bullet, fontSize);
  }

  for (const token of tokens) {
    const w = measureTokenWidth(doc, token, fontSize);
    if (currentW + w > maxWidth && current.length > 0) {
      flush();
      if (bulletPrefix) startContinuation();
    }
    current.push(token);
    currentW += w;
  }
  flush();
  return lines;
}

function drawObservationLine(
  doc: jsPDF,
  line: PdfObsLine,
  x: number,
  y: number,
  fontSize: number
): void {
  let cx = x;
  for (const token of line) {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
    const safe = pdfAsciiSafeText(token.text);
    doc.text(safe, cx, y);
    cx += doc.getTextWidth(safe);
  }
}

function drawObservationBox(
  doc: jsPDF,
  observations: MsrObservationEntry[],
  y: number,
  contentW: number,
  pageH: number
): void {
  if (observations.length === 0) return;

  const boxX = 12;
  const boxW = contentW;
  const headerH = 7;
  const padX = 4;
  const padY = 3;
  const lineH = 4.2;
  const fontSize = 8.5;
  const textW = boxW - padX * 2 - 2;

  const allLines: PdfObsLine[] = [];
  for (let o = 0; o < observations.length; o++) {
    const { text, children } = normalizeMsrObservation(observations[o]!);
    allLines.push(...wrapObservationParagraph(doc, text, textW, fontSize, '• '));
    for (const child of children ?? []) {
      allLines.push(...wrapObservationParagraph(doc, child, textW, fontSize, '    • '));
    }
    if (o < observations.length - 1) allLines.push([]);
  }

  const contentH = allLines.length * lineH + padY * 2;
  const boxH = headerH + contentH;
  const maxBoxH = pageH - 12 - 10 - y;
  const drawH = Math.min(boxH, maxBoxH);

  doc.setFillColor(OBS_BG.r, OBS_BG.g, OBS_BG.b);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.roundedRect(boxX, y, boxW, drawH, 2, 2, 'FD');

  doc.setFillColor(MSR_BLUE_RGB.r, MSR_BLUE_RGB.g, MSR_BLUE_RGB.b);
  doc.roundedRect(boxX + 1, y + 1, 36, headerH - 1, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('Observation', boxX + 4, y + 5.2);

  doc.setFontSize(fontSize);
  doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);

  let textY = y + headerH + padY + 2.5;
  const maxLines = Math.floor((drawH - headerH - padY * 2) / lineH);
  for (let i = 0; i < Math.min(allLines.length, maxLines); i++) {
    const line = allLines[i]!;
    if (line.length > 0) drawObservationLine(doc, line, boxX + padX + 1, textY, fontSize);
    textY += lineH;
  }

  doc.setTextColor(0, 0, 0);
}

function drawSummaryLines(
  doc: jsPDF,
  lines: string[],
  y: number,
  contentW: number
): number {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);
  let cy = y;
  for (const line of lines) {
    const wrapped = doc.splitTextToSize(pdfAsciiSafeText(line), contentW);
    doc.text(wrapped, 12, cy);
    cy += wrapped.length * 4.5 + 1;
  }
  doc.setTextColor(0, 0, 0);
  return cy + 2;
}

async function writeSubscriptionExcel(
  ws: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  data: AzureCustomerCostResult,
  month: number,
  year: number,
  startRow: number
): Promise<number> {
  let row = startRow;
  row = writeLabelValueTable(ws, row, 'Summary', [
    {
      label: 'Actual cost (MTD)',
      value: formatBillingCurrency(data.actualCostMtd, data.currency),
    },
    {
      label: 'Forecast month end',
      value:
        data.forecastMonthEnd != null
          ? formatBillingCurrency(data.forecastMonthEnd, data.currency)
          : '—',
    },
    { label: 'Period', value: monthYearLongLabel(month, year) },
  ]);

  row = await writeSingleChartImage(
    ws,
    workbook,
    AZURE_COST_SECTION_CONFIG['azure-subscription'].chartId,
    AZURE_COST_SECTION_CONFIG['azure-subscription'].chartLabel,
    row
  );

  row = writeTwoColumnTable(
    ws,
    row,
    1,
    'Daily cost series',
    'Date',
    'Accumulated cost',
    data.dailySeries.map((p) => ({
      c1: p.date,
      c2: formatBillingCurrency(p.accumulatedCost, data.currency),
    }))
  );

  row = writeTwoColumnTable(
    ws,
    row + 1,
    1,
    'Cost by service',
    'Service',
    'Cost',
    data.byService.map((s) => ({
      c1: s.serviceName,
      c2: formatBillingCurrency(s.cost, data.currency),
    }))
  );

  row = writeTwoColumnTable(
    ws,
    row + 1,
    4,
    'Engagements',
    'Engagement',
    'Cost',
    data.engagements.map((e) => ({
      c1: e.engagementName,
      c2: e.error ? '—' : formatBillingCurrency(e.actualCostMtd, e.currency),
    }))
  );

  if (data.skippedEngagements?.length) {
    row = writeTwoColumnTable(
      ws,
      row + 1,
      1,
      'Excluded engagements',
      'Engagement',
      'Reason',
      data.skippedEngagements.map((e) => ({ c1: e.engagementName, c2: e.reason }))
    );
  }

  return row;
}

async function writeThreeMonthExcel(
  ws: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  data: AzureCustomerCostResult,
  startRow: number
): Promise<number> {
  const observations = buildAzureCostObservations(
    data.last3Months ?? [],
    data.serviceCostDeltas ?? [],
    data.currency
  );

  let row = startRow;
  row = await writeSingleChartImage(
    ws,
    workbook,
    AZURE_COST_SECTION_CONFIG['azure-3months'].chartId,
    AZURE_COST_SECTION_CONFIG['azure-3months'].chartLabel,
    row
  );

  row = writeTwoColumnTable(
    ws,
    row,
    1,
    'Last 3 months',
    'Month',
    'Total cost',
    (data.last3Months ?? []).map((m) => ({
      c1: m.label,
      c2: formatBillingCurrency(m.totalCost, data.currency),
    }))
  );

  row = writeTwoColumnTable(
    ws,
    row + 1,
    1,
    'Service cost change (last 2 months)',
    'Service',
    'Change',
    (data.serviceCostDeltas ?? []).map((d) => ({
      c1: d.serviceName,
      c2: formatBillingCurrency(d.delta, data.currency),
    }))
  );

  row = writeObservations(ws, row + 1, observations);
  return row;
}

function analysisRowFill(
  trend: 'increased' | 'decreased' | 'unchanged'
): ExcelJS.Fill | undefined {
  if (trend === 'increased') return AZURE_ANALYSIS_INCREASED_FILL;
  if (trend === 'decreased') return AZURE_ANALYSIS_DECREASED_FILL;
  return undefined;
}

function writeThreeMonthAnalysisExcel(
  ws: ExcelJS.Worksheet,
  data: AzureCustomerCostResult,
  startRow: number
): number {
  const rows = data.serviceAnalysis3Months ?? [];
  if (rows.length === 0) {
    ws.getCell(startRow, 1).value = 'No Azure service analysis data for the last 3 months.';
    ws.mergeCells(startRow, 1, startRow, 8);
    return startRow + 2;
  }

  const monthLabels = rows[0]!.months.map((m) => m.label);
  let row = startRow;
  const totalCols = 1 + monthLabels.length * 2 + 1;

  const styleHeader = (cell: ExcelJS.Cell) => {
    cell.font = { bold: true, size: 10 };
    cell.fill = AZURE_ANALYSIS_HEADER_FILL;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = THIN_BORDER;
  };

  const servicesCol = ws.getCell(row, 1);
  servicesCol.value = 'Month\nServices';
  styleHeader(servicesCol);
  ws.mergeCells(row, 1, row + 1, 1);
  let col = 2;
  for (const label of monthLabels) {
    ws.getCell(row, col).value = label;
    styleHeader(ws.getCell(row, col));
    ws.mergeCells(row, col, row, col + 1);
    col += 2;
  }
  ws.getCell(row, totalCols).value = 'Cost Diff.';
  styleHeader(ws.getCell(row, totalCols));
  ws.mergeCells(row, totalCols, row + 1, totalCols);
  row += 1;

  col = 2;
  for (const label of monthLabels) {
    ws.getCell(row, col).value = 'Meter Count of Resource ID';
    styleHeader(ws.getCell(row, col));
    ws.getCell(row, col + 1).value = 'Sum of Cost USD';
    styleHeader(ws.getCell(row, col + 1));
    col += 2;
  }
  row += 1;

  const totals = rows[0]!.months.map((m) => ({
    label: m.label,
    meterCount: 0,
    cost: 0,
  }));

  for (const item of rows) {
    ws.getCell(row, 1).value = item.serviceName;
    const fill = analysisRowFill(item.trend);
    col = 2;
    item.months.forEach((m, idx) => {
      ws.getCell(row, col).value = m.meterCount;
      ws.getCell(row, col + 1).value = formatBillingCurrency(m.cost, data.currency);
      totals[idx]!.meterCount += m.meterCount;
      totals[idx]!.cost += m.cost;
      for (let c = 1; c <= totalCols; c++) {
        const cell = ws.getCell(row, c);
        cell.border = THIN_BORDER;
        cell.alignment = { horizontal: c === 1 ? 'left' : 'center', vertical: 'middle' };
        if (fill) cell.fill = fill;
      }
      col += 2;
    });
    ws.getCell(row, totalCols).value = formatBillingCurrency(item.costDiff, data.currency);
    ws.getCell(row, totalCols).font = { bold: item.trend !== 'unchanged' };
    row += 1;
  }

  ws.getCell(row, 1).value = 'Grand Total';
  styleHeader(ws.getCell(row, 1));
  col = 2;
  for (const total of totals) {
    ws.getCell(row, col).value = total.meterCount;
    styleHeader(ws.getCell(row, col));
    ws.getCell(row, col + 1).value = formatBillingCurrency(total.cost, data.currency);
    styleHeader(ws.getCell(row, col + 1));
    col += 2;
  }
  const prev = totals[totals.length - 2]?.cost ?? 0;
  const curr = totals[totals.length - 1]?.cost ?? 0;
  ws.getCell(row, totalCols).value = formatBillingCurrency(curr - prev, data.currency);
  styleHeader(ws.getCell(row, totalCols));

  return row + 2;
}

async function renderAzureSubscriptionPdf(
  doc: jsPDF,
  data: AzureCustomerCostResult,
  month: number,
  year: number
): Promise<void> {
  const config = AZURE_COST_SECTION_CONFIG['azure-subscription'];
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - 24;

  let y = drawSlideHeader(doc, data.customerName, config.title, contentW);
  y = drawSummaryLines(
    doc,
    [
      `Actual cost (MTD): ${formatBillingCurrency(data.actualCostMtd, data.currency)}`,
      `Forecast month end: ${
        data.forecastMonthEnd != null
          ? formatBillingCurrency(data.forecastMonthEnd, data.currency)
          : '—'
      }`,
      `Period: ${monthYearLongLabel(month, year)}`,
    ],
    y,
    contentW
  );

  const capture = await captureMsrElement(config.chartId);
  drawSingleChart(doc, capture, y, contentW);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(SLATE_400.r, SLATE_400.g, SLATE_400.b);
  doc.text(
    pdfAsciiSafeText(`Copyright © ${new Date().getFullYear()} Tenarai. All Rights Reserved.`),
    12,
    pageH - 8
  );
  doc.text('1', pageW - 12, pageH - 8, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

async function renderAzureThreeMonthPdf(doc: jsPDF, data: AzureCustomerCostResult): Promise<void> {
  const config = AZURE_COST_SECTION_CONFIG['azure-3months'];
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - 24;
  const observations = buildAzureCostObservations(
    data.last3Months ?? [],
    data.serviceCostDeltas ?? [],
    data.currency
  );

  let y = drawSlideHeader(doc, data.customerName, config.title, contentW);
  const capture = await captureMsrElement(config.chartId);
  y = drawSingleChart(doc, capture, y + 2, contentW);
  drawObservationBox(doc, observations, y, contentW, pageH);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(SLATE_400.r, SLATE_400.g, SLATE_400.b);
  doc.text(
    pdfAsciiSafeText(`Copyright © ${new Date().getFullYear()} Tenarai. All Rights Reserved.`),
    12,
    pageH - 8
  );
  doc.text('1', pageW - 12, pageH - 8, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

async function renderAzureThreeMonthAnalysisPdf(
  doc: jsPDF,
  data: AzureCustomerCostResult
): Promise<void> {
  const config = AZURE_COST_SECTION_CONFIG['azure-3months-analysis'];
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - 24;

  let y = drawSlideHeader(doc, data.customerName, config.title, contentW);
  const capture = await captureAnalysisTableElement(config.chartId);

  if (capture?.dataUrl) {
    await drawAnalysisTableCapture(doc, capture, y + 2, contentW, pageH);
  } else if ((data.serviceAnalysis3Months ?? []).length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(SLATE_600.r, SLATE_600.g, SLATE_600.b);
    doc.text('No Azure service analysis data for the last 3 months.', 12, y + 4);
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addAzurePdfFooter(doc, i, pageCount);
  }
}

export async function exportAzureCostSectionPdf(
  section: AzureCostSectionKey,
  data: AzureCustomerCostResult,
  dateFrom: string,
  dateTo: string,
  month: number,
  year: number
): Promise<AzureCostSectionExport> {
  return withAzureCostExportCapture(async () => {
    const config = AZURE_COST_SECTION_CONFIG[section];
    const doc = new jsPDF('l', 'mm', 'a4');

    if (section === 'azure-subscription') {
      await renderAzureSubscriptionPdf(doc, data, month, year);
    } else if (section === 'azure-3months') {
      await renderAzureThreeMonthPdf(doc, data);
    } else {
      await renderAzureThreeMonthAnalysisPdf(doc, data);
    }

    return {
      blob: doc.output('blob'),
      fileName: msrSectionFilename(config.fileSlug, data.customerName, dateFrom, dateTo, 'pdf'),
    };
  });
}

function configureAnalysisExcelColumns(ws: ExcelJS.Worksheet, monthCount: number): void {
  const totalCols = 1 + monthCount * 2 + 1;
  ws.getColumn(1).width = 28;
  for (let i = 0; i < monthCount; i++) {
    const meterCol = 2 + i * 2;
    ws.getColumn(meterCol).width = 14;
    ws.getColumn(meterCol + 1).width = 16;
  }
  ws.getColumn(totalCols).width = 14;
}

export async function exportAzureCostSectionExcel(
  section: AzureCostSectionKey,
  data: AzureCustomerCostResult,
  dateFrom: string,
  dateTo: string,
  month: number,
  year: number
): Promise<AzureCostSectionExport> {
  return withAzureCostExportCapture(async () => {
    const config = AZURE_COST_SECTION_CONFIG[section];
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Pulse';
    workbook.created = new Date();

    const ws = workbook.addWorksheet('Report', { views: [{ showGridLines: true }] });
    const analysisMonthCount =
      section === 'azure-3months-analysis'
        ? (data.serviceAnalysis3Months?.[0]?.months.length ?? 3)
        : 0;
    const titleMergeCols =
      section === 'azure-3months-analysis' ? 1 + analysisMonthCount * 2 + 1 : 5;

    if (section === 'azure-3months-analysis') {
      configureAnalysisExcelColumns(ws, analysisMonthCount);
    } else {
      ws.getColumn(1).width = 36;
      ws.getColumn(2).width = 18;
      ws.getColumn(3).width = 4;
      ws.getColumn(4).width = 36;
      ws.getColumn(5).width = 18;
    }

    let row = 1;
    ws.getCell(row, 1).value = data.customerName;
    ws.getCell(row, 1).font = { bold: true, size: 16, color: { argb: MSR_BLUE } };
    ws.mergeCells(row, 1, row, titleMergeCols);
    row += 1;

    ws.getCell(row, 1).value = config.title;
    ws.getCell(row, 1).font = { bold: true, size: 11, color: { argb: 'FF475569' } };
    ws.mergeCells(row, 1, row, titleMergeCols);
    row += 2;

    ws.getCell(row, 1).value = 'Period';
    ws.getCell(row, 1).font = { bold: true };
    ws.getCell(row, 2).value = `${dateFrom.slice(0, 10)} to ${dateTo.slice(0, 10)}`;
    row += 2;

    if (section === 'azure-subscription') {
      await writeSubscriptionExcel(ws, workbook, data, month, year, row);
    } else if (section === 'azure-3months') {
      await writeThreeMonthExcel(ws, workbook, data, row);
    } else {
      writeThreeMonthAnalysisExcel(ws, data, row);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    return {
      blob: new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      fileName: msrSectionFilename(config.fileSlug, data.customerName, dateFrom, dateTo, 'xlsx'),
    };
  });
}
